import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';



// Command Center's "To Do" kanban column — a fixed, well-known status id (see
// KANBAN_STATUS_IDS in CommandIssuePanel.tsx). StatusId is a non-nullable Guid
// on the backend, so a real value must always be sent or the task lands with
// Guid.Empty and won't show up in any real column.
const KANBAN_TODO_STATUS_ID = '11111111-1111-1111-1111-111111111111';

export interface InsuredRecord {
  name: string;
  phone: string;
  databaseId: string;
}

interface UseCallConclusionArgs {
  parentCallSid: string;
  initialInsuredId?: string;
  initialContactName?: string;
  initialCallNotes?: string;
  transcript?: string;
  summary?: string;
  phoneNumber?: string;
  onInsuredSelected?: (insured: InsuredRecord) => void;
  onSaved?: (result: { callNotes: string; insuredId: string; contactName: string }) => void;
}

// Shared save/insured-attach logic behind the dialer's "Save & Close" (live call
// summary) and the call-history detail panel — both need the same conclusion
// save + AMS/Command Center task creation, just with different surrounding UI.
export function useCallConclusion({
  parentCallSid,
  initialInsuredId = '',
  initialContactName = '',
  initialCallNotes = '',
  transcript = '',
  summary = '',
  phoneNumber,
  onInsuredSelected: onInsuredSelectedProp,
  onSaved,
}: UseCallConclusionArgs) {
  const { showSuccess, showError } = useToast();

  const [insuredId, setInsuredId] = useState(initialInsuredId);
  const [contactName, setContactName] = useState(initialContactName);
  const [insuredSearchTerm, setInsuredSearchTerm] = useState('');
  const [insuredSearchResults, setInsuredSearchResults] = useState<InsuredRecord[]>([]);
  const [insuredSearchLoading, setInsuredSearchLoading] = useState(false);
  const [insuredError, setInsuredError] = useState('');

  const [callNotes, setCallNotes] = useState(initialCallNotes);
  const [callNotesError, setCallNotesError] = useState('');

  const [createTask, setCreateTask] = useState(false);
  const [createCommandCenterTask, setCreateCommandCenterTask] = useState(false);
  const [reminderTask, setReminderTask] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [taskError, setTaskError] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (insuredId || insuredSearchTerm.trim() === '') {
      setInsuredSearchResults([]);
      return;
    }
    setInsuredSearchLoading(true);
    const delay = setTimeout(() => {
      const nowCertsToken = localStorage.getItem('amsToken') || '';
      fetch(
        `${(process.env.REACT_APP_NOWCERTSAPI || 'https://nowcertsapi-stage.insurancegig.com')}/api/nowcert/customerlist?searchText=${encodeURIComponent(insuredSearchTerm)}&nowCertsToken=${encodeURIComponent(nowCertsToken)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('toolboxToken') || ''}`,
            'Content-Type': 'application/json',
          },
        }
      )
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((data) => setInsuredSearchResults(Array.isArray(data) ? data : []))
        .catch(() => setInsuredSearchResults([]))
        .finally(() => setInsuredSearchLoading(false));
    }, 500);
    return () => clearTimeout(delay);
  }, [insuredId, insuredSearchTerm]);

  const onInsuredSelected = (insured: InsuredRecord) => {
    setInsuredId(insured.databaseId);
    setContactName(insured.name);
    setInsuredError('');
    setInsuredSearchTerm('');
    onInsuredSelectedProp?.(insured);
  };

  const onCreateTaskToggle = (checked: boolean) => {
    setCreateTask(checked);
    if (checked) setReminderTask((prev) => summary || prev);
  };

  const postCommandCenterTask = async () => {
    const authToken = localStorage.getItem('authToken') || '';
    const title = contactName
      ? `Follow-up: ${contactName}`
      : phoneNumber
        ? `Follow-up call — +${phoneNumber}`
        : 'Dialer follow-up task';

    const descriptionParts = [callNotes.trim()];
    if (dueDate) descriptionParts.push(`Due: ${dueDate}`);
    const callMeta = [contactName, phoneNumber ? `+${phoneNumber}` : null].filter(Boolean);
    if (callMeta.length) descriptionParts.push(`Related call: ${callMeta.join(' · ')}`);

    const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/task/upsert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({
        id: 0,
        taskNumber: null,
        statusId: KANBAN_TODO_STATUS_ID,
        title,
        description: descriptionParts.join('\n\n'),
        comments: null,
        assigneeId: Number(localStorage.getItem('userId')) || 0,
        assigneeType: 0, // User
        lastAssigneeType: 0,
        position: 0,
        source: 2, // Voice
        categoryId: 7, // Task
        agentName: '',
        agentId: '',
        files: null,
        transactionId: null,
      }),
    });

    if (!res.ok) {
      let msg = 'Failed to create task in Command Center.';
      try {
        const body = await res.json();
        msg = body?.errorMessage || body?.message || (typeof body === 'string' ? body : msg);
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      throw new Error(msg);
    }
  };

  const save = async (): Promise<boolean> => {
    setCallNotesError('');
    setTaskError('');
    setInsuredError('');
    if (!insuredId) {
      setInsuredError('Please select an insured before saving.');
      return false;
    }
    if (callNotes.trim() === '') {
      setCallNotesError('Please enter call notes…');
      return false;
    }
    if ((createTask || createCommandCenterTask) && reminderTask.trim() === '') {
      setTaskError('Please enter task details before creating a task.');
      return false;
    }

    setSaving(true);
    try {
      const res = await fetch(`${(process.env.REACT_APP_DataFabric_Url || 'https://datafabricapi-stage.insurancegig.com')}/api/twilio/conclusion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ParentCallSid: parentCallSid,
          Transcription: transcript,
          Summary: summary,
          CallNotes: callNotes,
          Task: reminderTask,
          TaskReminderDate: dueDate,
          UserId: Number(localStorage.getItem('userId')) || 0,
          CreateTask: createTask,
          NcToken: localStorage.getItem('amsToken') || '',
          InsuredId: insuredId,
        }),
      });
      if (!res.ok) throw new Error();

      if (createCommandCenterTask) {
        try {
          await postCommandCenterTask();
          showSuccess('Task created in Command Center.');
        } catch (e: any) {
          showError(e?.message || 'Failed to create task in Command Center.');
        }
      }

      onSaved?.({ callNotes, insuredId, contactName });
      return true;
    } catch {
      showError('Failed to save call conclusion. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    insuredId,
    contactName,
    insuredSearchTerm,
    setInsuredSearchTerm,
    insuredSearchResults,
    insuredSearchLoading,
    insuredError,
    onInsuredSelected,

    callNotes,
    setCallNotes,
    callNotesError,

    createTask,
    onCreateTaskToggle,
    createCommandCenterTask,
    setCreateCommandCenterTask,
    reminderTask,
    setReminderTask,
    dueDate,
    setDueDate,
    taskError,

    saving,
    save,
  };
}

export type CallConclusion = ReturnType<typeof useCallConclusion>;

